import { UseFormReturn } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { HighlightEditor } from '@/components/ui/highlight-editor';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { CheckCircle2, XCircle, AlertCircle } from 'lucide-react';

interface SurveySummaryProps {
  form: UseFormReturn<any>;
}

export function SurveySummary({ form }: SurveySummaryProps) {
  // Watch all form values to generate summary
  const formValues = form.watch();

  // Helper function to check if a required field is filled
  const isFieldFilled = (value: any) => {
    if (typeof value === 'string') return value && value.length > 0;
    if (typeof value === 'number') return true;
    if (typeof value === 'boolean') return true;
    if (Array.isArray(value)) return value.length > 0;
    if (value instanceof Date) return true;
    // Handle timestamp/date objects
    if (value && typeof value === 'object' && value.toString() !== '[object Object]') {
      return true;
    }
    return false;
  };

  // Generate required items checklist
  const generateRequiredItemsChecklist = () => {
    const checklist = [];

    // Survey Header Requirements
    checklist.push({
      section: 'Survey Header',
      items: [
        {
          label: 'Site Assessor Name',
          filled: isFieldFilled(formValues.siteAssessorName),
          required: true,
        },
        { label: 'Survey Date', filled: isFieldFilled(formValues.surveyDate), required: true },
      ],
    });

    // Electric Requirements
    checklist.push({
      section: 'Electric Information',
      items: [
        {
          label: 'Earthing System',
          filled: isFieldFilled(formValues.earthingSystem),
          required: true,
        },
        { label: 'Main Fuse Size', filled: isFieldFilled(formValues.mainFuseSize), required: true },
        { label: 'AC SPD Present', filled: isFieldFilled(formValues.acSpdPresent), required: true },
        {
          label: 'Electric Photos',
          filled: isFieldFilled(formValues.photoIncomer) || isFieldFilled(formValues.photoMainDb),
          required: true,
        },
      ],
    });

    // Roof Information Requirements
    checklist.push({
      section: 'Roof Information',
      items: [
        {
          label: 'Roof Type',
          filled: formValues.roofs?.some((roof: any) => isFieldFilled(roof.roofType)),
          required: true,
        },
        {
          label: 'Roof Pitch',
          filled: formValues.roofs?.some((roof: any) => isFieldFilled(roof.pitch)),
          required: true,
        },
        {
          label: 'Scaffolding Required',
          filled: isFieldFilled(formValues.scaffolding),
          required: true,
        },
        {
          label: 'Roof Photos',
          filled: formValues.roofs?.some((roof: any) => isFieldFilled(roof.roofImage1)),
          required: true,
        },
      ],
    });

    // Installation Requirements
    checklist.push({
      section: 'Installation & Equipment',
      items: [
        { label: 'Inverter DB', filled: isFieldFilled(formValues.inverterDb), required: true },
        { label: 'Proposed Area', filled: isFieldFilled(formValues.proposedArea), required: true },
        {
          label: 'Equipment Location Photo',
          filled: isFieldFilled(formValues.proposedAreaPhoto),
          required: true,
        },
        {
          label: 'AC Circuits',
          filled: formValues.inverterCircuits?.length > 0,
          required: false,
        },
      ],
    });

    // DC & Additional Works Requirements
    checklist.push({
      section: 'DC & Additional Works',
      items: [
        { label: 'DC Strings', filled: isFieldFilled(formValues.dcStrings), required: true },
        { label: 'Cable CSA', filled: isFieldFilled(formValues.dcCableCsa), required: true },
        { label: 'Longest Run', filled: isFieldFilled(formValues.dcLongestRun), required: true },
        {
          label: 'Gateway Earthing',
          filled: isFieldFilled(formValues.gatewayEarthing),
          required: true,
        },
      ],
    });

    return checklist;
  };

  // Compile scope of works from all tabs
  const compileScopeOfWorks = () => {
    const scopeSections = [];

    if (isFieldFilled(formValues.scopeOfWorksRoof)) {
      scopeSections.push({
        title: 'Roof Works',
        content: formValues.scopeOfWorksRoof,
      });
    }

    if (isFieldFilled(formValues.scopeOfWorksInverterAC)) {
      scopeSections.push({
        title: 'Inverter and AC Works',
        content: formValues.scopeOfWorksInverterAC,
      });
    }

    if (isFieldFilled(formValues.scopeOfWorksDcRuns)) {
      scopeSections.push({
        title: 'DC Runs Works',
        content: formValues.scopeOfWorksDcRuns,
      });
    }

    if (isFieldFilled(formValues.scopeOfWorksGroundWorks)) {
      scopeSections.push({
        title: 'Ground Works',
        content: formValues.scopeOfWorksGroundWorks,
      });
    }

    return scopeSections;
  };

  // Compile additional works summary
  const compileAdditionalWorks = () => {
    const additionalWorks = [];

    if (formValues.evChargerRequired) {
      additionalWorks.push({
        item: 'EV Charger',
        details: formValues.evChargerDetails || 'Required',
      });
    }

    if (formValues.immersionCircuitRequired) {
      additionalWorks.push({
        item: 'Immersion Circuit iBoost',
        details: formValues.immersionCircuitDetails || 'Required',
      });
    }

    if (formValues.birdguardRequired) {
      additionalWorks.push({
        item: 'Birdguard',
        details: formValues.birdguardDetails || 'Required',
      });
    }

    return additionalWorks;
  };

  const requiredItemsChecklist = generateRequiredItemsChecklist();
  const scopeOfWorks = compileScopeOfWorks();
  const additionalWorks = compileAdditionalWorks();

  // Calculate completion percentage
  const totalRequiredItems = requiredItemsChecklist.reduce(
    (acc, section) => acc + section.items.filter((item) => item.required).length,
    0
  );
  const completedRequiredItems = requiredItemsChecklist.reduce(
    (acc, section) => acc + section.items.filter((item) => item.required && item.filled).length,
    0
  );
  const completionPercentage =
    totalRequiredItems > 0 ? Math.round((completedRequiredItems / totalRequiredItems) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Completion Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Survey Completion Status</span>
            <span className="text-2xl font-bold">{completionPercentage}%</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
            <div
              className="bg-blue-600 h-4 rounded-full transition-all duration-300"
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
          <p className="text-sm text-muted-foreground">
            {completedRequiredItems} of {totalRequiredItems} required items completed
          </p>
        </CardContent>
      </Card>

      {/* Required Items Checklist */}
      <Card>
        <CardHeader>
          <CardTitle>Required Items Checklist</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {requiredItemsChecklist.map((section, sectionIndex) => (
              <div key={sectionIndex}>
                <h4 className="font-medium text-sm mb-3">{section.section}</h4>
                <div className="space-y-2">
                  {section.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-center gap-2">
                      {item.filled ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      ) : item.required ? (
                        <XCircle className="h-4 w-4 text-red-600" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-gray-400" />
                      )}
                      <span
                        className={`text-sm ${item.filled ? 'text-green-700' : 'text-gray-600'}`}
                      >
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Scope of Works Compilation */}
      {scopeOfWorks.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Scope of Works Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {scopeOfWorks.map((scope, index) => (
                <div key={index} className="border-b last:border-b-0 pb-4 last:pb-0">
                  <h4 className="font-medium text-sm mb-2">{scope.title}</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{scope.content}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Additional Works Summary */}
      {additionalWorks.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Additional Works Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {additionalWorks.map((work, index) => (
                <div key={index} className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
                  <div>
                    <span className="font-medium text-sm">{work.item}</span>
                    {work.details !== 'Required' && (
                      <span className="text-sm text-gray-600"> - {work.details}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Special Requirements/Notes */}
      <Card>
        <CardHeader>
          <CardTitle>Special Requirements/Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <FormField
            control={form.control}
            name="specialRequirements"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <HighlightEditor
                    value={field.value || ''}
                    onChange={field.onChange}
                    placeholder="Add any special requirements or additional notes for this survey..."
                    minHeight="150px"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>

      {/* Survey Metadata */}
      <Card>
        <CardHeader>
          <CardTitle>Survey Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium">Customer Name:</span> {formValues.clientName || 'N/A'}
            </div>
            <div>
              <span className="font-medium">Customer Address:</span>{' '}
              {formValues.clientAddress || 'N/A'}
            </div>
            <div>
              <span className="font-medium">Site Assessor:</span>{' '}
              {formValues.siteAssessorName || 'N/A'}
            </div>
            <div>
              <span className="font-medium">Survey Date:</span> {formValues.surveyDate || 'N/A'}
            </div>
            <div>
              <span className="font-medium">AC SPD Present:</span>{' '}
              {formValues.acSpdPresent === 'YES'
                ? 'Yes'
                : formValues.acSpdPresent === 'NO'
                  ? 'No'
                  : formValues.acSpdPresent === 'REQUIRED'
                    ? 'Required'
                    : 'Not specified'}
            </div>
            <div>
              <span className="font-medium">AC Circuits:</span>{' '}
              {formValues.inverterCircuits?.length > 0
                ? `${formValues.inverterCircuits.length} circuit(s)`
                : 'None added'}
            </div>
            {formValues.surveyLatitude && formValues.surveyLongitude && (
              <div className="md:col-span-2">
                <span className="font-medium">GPS Location:</span>{' '}
                {formValues.surveyLatitude.toFixed(6)}, {formValues.surveyLongitude.toFixed(6)}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
